import type { Document } from './document.ts';
import type { NodeData, NodeId, NodeProps, Op } from './types.ts';
import { newId } from './util.ts';
import { TEXT_STYLE_KEYS } from './tokens.ts';

/** Keys never copied from a component to its instances. */
const STRUCTURAL = new Set(['id', 'type', 'parent', 'index', 'sourceId', 'overrides', 'componentId']);
/** Additional keys that belong to the instance root itself (placement). */
const ROOT_LOCAL = new Set(['x', 'y', 'rotation', 'name', 'locked', 'visible', 'layoutPositioning', 'layoutSizingH', 'layoutSizingV', 'flipX', 'flipY', 'variableModes']);

/** Props whose values come from styles/variables; the token resolver computes them per instance (modes differ). */
function tokenControlled(n: NodeData): Set<string> {
  const out = new Set<string>();
  if (n.fillStyleId) out.add('fills');
  if (n.strokeStyleId) out.add('strokes');
  if (n.effectStyleId) out.add('effects');
  if (n.textStyleId) for (const k of TEXT_STYLE_KEYS) out.add(k);
  for (const key of Object.keys(n.boundVariables ?? {})) {
    if (key === 'fill' || (key === 'textColor' && n.type === 'TEXT')) out.add('fills');
    else if (key === 'stroke') out.add('strokes');
    else out.add(key);
  }
  return out;
}

function nearestComponent(doc: Document, id: NodeId): NodeData | undefined {
  let n = doc.get(id);
  while (n) {
    if (n.type === 'COMPONENT') return n;
    n = doc.get(n.parent);
  }
  return undefined;
}

export function instancesOf(doc: Document, componentId: NodeId): NodeData[] {
  const out: NodeData[] = [];
  for (const n of doc.nodes.values()) if (n.type === 'INSTANCE' && n.componentId === componentId) out.push(n);
  return out;
}

/** Copy a component subtree into a fresh instance (returns create ops). */
export function instantiate(doc: Document, componentId: NodeId, parent: NodeId, index: string, props: NodeProps = {}, id?: NodeId): { id: NodeId; ops: Op[] } {
  const comp = doc.must(componentId);
  if (comp.type !== 'COMPONENT') throw new Error(`${componentId} is not a component`);
  const ops: Op[] = [];
  const rootId = id ?? newId();
  const map = new Map<NodeId, NodeId>([[comp.id, rootId]]);
  doc.walk(comp.id, (n) => {
    if (n.id === comp.id) {
      const { id: _i, ...rest } = structuredClone(n);
      ops.push({
        op: 'create',
        node: { ...rest, id: rootId, type: 'INSTANCE', parent, index, componentId: comp.id, sourceId: undefined, overrides: [], name: comp.name, ...props } as NodeData,
      });
      return;
    }
    const id = newId();
    map.set(n.id, id);
    ops.push({ op: 'create', node: { ...structuredClone(n), id, parent: map.get(n.parent!)!, sourceId: n.id, overrides: [] } });
  });
  return { id: rootId, ops };
}

function syncProps(src: NodeData, dst: NodeData, isRoot: boolean): NodeProps | null {
  const overrides = new Set([...(dst.overrides ?? []), ...tokenControlled(src), ...Object.keys(src.propRefs ?? {})]);
  // an instance's own wiring to its parent component's properties is local to it
  if (isRoot) { overrides.add('propValues'); overrides.add('propRefs'); overrides.add('exposedInstance'); overrides.add('codeConnect'); }
  const out: Record<string, unknown> = {};
  let any = false;
  const keys = new Set([...Object.keys(src), ...Object.keys(dst)]);
  for (const k of keys) {
    if (STRUCTURAL.has(k) || overrides.has(k) || (isRoot && ROOT_LOCAL.has(k))) continue;
    if (k === 'pluginData') continue;
    const sv = (src as unknown as Record<string, unknown>)[k];
    const dv = (dst as unknown as Record<string, unknown>)[k];
    if (JSON.stringify(sv) !== JSON.stringify(dv)) {
      out[k] = sv;
      any = true;
    }
  }
  return any ? (out as NodeProps) : null;
}

/** Ops that bring every instance of the touched components back in sync. */
export function syncInstances(doc: Document, changed: Iterable<NodeId>, deletedParents: Iterable<NodeId> = []): Op[] {
  const comps = new Set<NodeId>();
  for (const id of [...changed, ...deletedParents]) {
    const c = nearestComponent(doc, id);
    if (c) comps.add(c.id);
  }
  const ops: Op[] = [];
  for (const cid of comps) {
    const comp = doc.must(cid);
    for (const inst of instancesOf(doc, cid)) {
      if (doc.isAncestor(inst.id, cid)) continue; // avoid recursion
      const bySource = new Map<NodeId, NodeData>();
      doc.walk(inst.id, (n) => {
        if (n.id !== inst.id && n.sourceId) bySource.set(n.sourceId, n);
        // don't descend into nested instances: they sync with their own component
        if (n.id !== inst.id && n.type === 'INSTANCE') return false;
      });
      const rootProps = syncProps(comp, inst, true);
      if (rootProps) ops.push({ op: 'set', id: inst.id, props: rootProps });
      const seen = new Set<NodeId>();
      const target = new Map<NodeId, NodeId>([[comp.id, inst.id]]);
      doc.walk(comp.id, (n) => {
        if (n.id === comp.id) return;
        const parentTarget = target.get(n.parent!);
        if (!parentTarget) return false;
        const existing = bySource.get(n.id);
        if (existing) {
          seen.add(n.id);
          target.set(n.id, existing.id);
          const p = syncProps(n, existing, false) ?? {};
          if (existing.parent !== parentTarget) (p as NodeProps).parent = parentTarget;
          if (existing.index !== n.index) (p as NodeProps).index = n.index;
          if (Object.keys(p).length) ops.push({ op: 'set', id: existing.id, props: p });
        } else {
          const id = newId();
          target.set(n.id, id);
          seen.add(n.id);
          ops.push({ op: 'create', node: { ...structuredClone(n), id, parent: parentTarget, sourceId: n.id, overrides: [] } });
        }
        if (n.type === 'INSTANCE') return false;
      });
      for (const [src, n] of bySource) {
        if (!seen.has(src) && doc.has(n.id)) {
          // only delete top-most stale nodes (children go with them)
          const parentStale = n.parent && n.parent !== inst.id && [...bySource.values()].some((m) => m.id === n.parent && !seen.has(m.sourceId!));
          if (!parentStale) ops.push({ op: 'delete', id: n.id });
        }
      }
    }
  }
  return ops;
}

/** The instance a node belongs to (nearest INSTANCE ancestor-or-self). */
function owningInstance(doc: Document, id: NodeId): NodeData | undefined {
  let n = doc.get(id);
  while (n) {
    if (n.type === 'INSTANCE') return n;
    n = doc.get(n.parent);
  }
  return undefined;
}

/** Ops that apply instance property values (and component defaults) to bound layers. */
export function resolveComponentProps(doc: Document, changed: Iterable<NodeId>): Op[] {
  const instances = new Set<NodeId>();
  for (const id of changed) {
    const n = doc.get(id);
    if (!n) continue;
    if (n.type === 'COMPONENT') for (const i of instancesOf(doc, id)) instances.add(i.id);
    // an instance nested in another resolves its own properties (e.g. exposed nested instances)
    if (n.type === 'INSTANCE') instances.add(n.id);
    const inst = owningInstance(doc, id);
    if (inst) instances.add(inst.id);
  }
  const ops: Op[] = [];
  for (const iid of instances) {
    const inst = doc.must(iid);
    const comp = inst.componentId ? doc.get(inst.componentId) : undefined;
    const defs = comp?.componentProps ?? {};
    doc.walk(iid, (n) => {
      const refs = n.propRefs;
      const nested = n.id !== iid && n.type === 'INSTANCE';
      const p: NodeProps = {};
      // nested instance swap (driven by the outer instance's property)
      if (nested) {
        const prop = refs?.componentId;
        const v = prop && defs[prop] ? (inst.propValues?.[prop] ?? defs[prop].default) : undefined;
        if (typeof v === 'string' && v && v !== n.componentId && doc.get(v)?.type === 'COMPONENT') {
          for (const c of doc.children(n.id)) ops.push({ op: 'delete', id: c.id });
          const { ops: made } = instantiate(doc, v, n.parent!, n.index);
          const root = (made[0] as Extract<Op, { op: 'create' }>).node;
          Object.assign(p, { componentId: v, width: root.width, height: root.height, fills: root.fills });
          if (!n.overrides?.includes('name')) p.name = root.name;
          for (const op of made.slice(1)) {
            const c = (op as Extract<Op, { op: 'create' }>).node;
            ops.push({ op: 'create', node: { ...c, parent: c.parent === root.id ? n.id : c.parent } });
          }
        }
      }
      for (const [field, prop] of Object.entries(refs ?? {}) as ['visible' | 'characters' | 'componentId', string][]) {
        const def = defs[prop];
        if (!def) continue;
        const v = inst.propValues?.[prop] ?? def.default;
        if (field === 'visible' && typeof v === 'boolean' && n.visible !== v) p.visible = v;
        if (field === 'characters' && typeof v === 'string' && n.characters !== v) p.characters = v;
      }
      if (Object.keys(p).length) ops.push({ op: 'set', id: n.id, props: p });
      if (nested) return false; // its own children resolve against its own component
    });
  }
  return ops;
}

/** Keys edited on a node inside an instance become overrides so syncs don't clobber them. */
export function overrideKeysFor(doc: Document, id: NodeId, props: NodeProps): string[] | null {
  const n = doc.get(id);
  if (!n) return null;
  const insideInstance = n.type === 'INSTANCE' || doc.ancestors(id).some((a) => a.type === 'INSTANCE');
  if (!insideInstance) return null;
  const keys = Object.keys(props).filter((k) => !STRUCTURAL.has(k) && !(n.type === 'INSTANCE' && ROOT_LOCAL.has(k)));
  if (!keys.length) return null;
  const merged = new Set([...(n.overrides ?? []), ...keys]);
  if (merged.size === (n.overrides ?? []).length) return null;
  return [...merged];
}
